import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  useTheme,
  Checkbox,
  Select,
  CircularProgress,
  InputLabel,
  ListItemText,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";
import SubHeader from "../../../../../components/SubHeader";
import SalaryManagementList from "./SalaryManagementList";
import styles from "./style";
import { useAlert } from "../../../../../context/AlertContext";
import { LoadingButton } from "@mui/lab";
import Modal from "../../../../../components/Modal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CustomizedRadio } from "../../../../../components/CustomRadio";

// ------------------------------------------------------------------------------------------------
// Salary Main page
// ------------------------------------------------------------------------------------------------

const SalaryManagementPage = ({
  data,
  handleSearch,
  count,
  page,
  setPage,
  handleUpdate,
  handleProcessAll,
  handleBonus,
  handlePrintAll,
  handleEmailAll,
  loading,
  loader,
  dataAll,
  DepartmentPayroll,
  empData,
  depData,
  SearchEmployee,
  employeeData,
  departmentData,
  payslipPDF,
  handleDate,
  popupMsg,
  setHolidayOpen,
  holidayOpen,
  handleShowPdf,
  isProcessed,
  isPayslipAccessDisabled,
}) => {
  const [model, setModel] = useState(false);
  const theme = useTheme();
  const { showAlert } = useAlert();
  const handleFilter = () => {
    showAlert("Development in Progress", "success");
  };
  const radioLabel = [
    { label: "Fixed", value: "Fixed" },
    { label: "Percentage", value: "Percentage" },
  ];

  const permissionObj = localStorage.getItem("permission");
  const permission = JSON.parse(permissionObj);
  const role = localStorage.getItem("role");
  const [selectedOptions, setSelectedOptions] = useState(
    data?.employeeDetails || []
  );
  const [empDataId, setEmpDataId] = useState(employeeData)
  const [department, setDepartment] = useState(data?.department);
  const [date, setDate] = useState(dayjs());
  const [to, setTo] = useState(dayjs());
  const [fixed, setFixed] = useState("Fixed");
  const [bonus, setBonus] = useState();
  const [bonusName, setBonusName] = useState("");
  const [percentageType, setPercentageType] = useState(""); // For "Gross" or "Salary" dropdown

  const getEmployeeEmail = (employee) =>
    employee?.personalInfo?.email || employee?.email || "";
  const getEmployeeId = (employee) =>
    employee?._id || employee?.id || getEmployeeEmail(employee)?.toLowerCase();
  const getEmployeeDepartment = (employee) =>
    employee?.personalInfo?.department || employee?.department || "";
  const normalizeEmployeeList = (source) => {
    if (Array.isArray(source)) return source.filter(Boolean);
    if (Array.isArray(source?.data)) return source.data.filter(Boolean);
    return [];
  };
  const mergeUniqueEmployees = (...lists) => {
    const seenIds = new Set();
    return lists.flat().reduce((result, employee) => {
      const key = getEmployeeId(employee);
      if (!key || seenIds.has(key)) return result;
      seenIds.add(key);
      result.push(employee);
      return result;
    }, []);
  };

  const allEmployees = normalizeEmployeeList(empData);
  const searchedEmployees = normalizeEmployeeList(employeeData);
  const departmentEmployees = normalizeEmployeeList(depData);

  const getUniqueEmails = (employees = []) => {
    const seenEmails = new Set();
    return (employees || []).reduce((result, employee) => {
      const email = getEmployeeEmail(employee)?.trim();
      const normalizedEmail = email?.toLowerCase();

      if (email && !seenEmails.has(normalizedEmail)) {
        seenEmails.add(normalizedEmail);
        result.push(email);
      }

      return result;
    }, []);
  };

  const displayEmailList = getUniqueEmails([
    ...(selectedOptions || []),
    ...(department && department !== "All" ? departmentEmployees : []),
  ]);
  const selectedEmailsText = displayEmailList.join(", ");
  const hasSelectedEmails = displayEmailList.length > 0;

  const isEmployee =
    role === "Employee" || role === "employee";

  const handleText = (text) => {
    handleSearch(text);
  };
  const handleSort = (sort) => {
    handleSearch("", sort);
  };
  const handleNavigate = () => {
    handleProcessAll();
  };

  const handleChange = (event, newValue) => {
    const filteredNewValues = newValue.filter(
      (newOption) =>
        newOption?._id &&
        !selectedOptions.some(
          (prevOption) => prevOption?._id === newOption?._id
        )
    );
    setSelectedOptions([...selectedOptions, ...filteredNewValues]);
  };

  const handleDepartmentChange = (e) => {
    const selectedDept = e.target.value;
    const employeeList = allEmployees;
    setDepartment(selectedDept);
    if (selectedDept === "All") {
      setSelectedOptions(employeeList.filter(Boolean));
    } else {
      DepartmentPayroll(selectedDept);

      const deptEmployees = employeeList.filter(
        (emp) => getEmployeeDepartment(emp) === selectedDept
      );
      setSelectedOptions(deptEmployees);
    }
  };

  useEffect(() => {
    const departmentOptions =
      department && department !== "All" ? departmentEmployees : [];
    const options =
      department === "All"
        ? mergeUniqueEmployees(allEmployees, searchedEmployees)
        : mergeUniqueEmployees(searchedEmployees, departmentOptions);

    setEmpDataId(options);
  }, [department, empData, employeeData, depData]);

  useEffect(() => {
    if (!department || department === "All") return;

    const selectedDeptEmployees = departmentEmployees.filter(
      (employee) => getEmployeeDepartment(employee) === department
    );

    if (!selectedDeptEmployees.length) return;
    setSelectedOptions(selectedDeptEmployees);
  }, [department, depData]);

  return (
    <>
      <Box sx={{ ...styles.sectionGrid, flexWrap: "wrap", gap: "10px" }}>
        <Box>
          <Typography sx={styles.leaveHeading}>{isEmployee ? "My Payslips" : "Salary Management"}</Typography>
          {isEmployee && (
            <Typography sx={styles.periodStatText}>
              {isPayslipAccessDisabled
                ? "Your payslip access has been disabled. Please contact your employer if you need a payslip."
                : `Payslips Issued: ${count ?? 0}`}
            </Typography>
          )}
        </Box>
        <Box sx={styles.btnSection}>
          {/* Hide Add Bonus button when ALL items have isPaid: true */}
          {!isEmployee && data?.length > 0 && !data?.every(item => item?.isPaid === true) && (
            <Box sx={styles.btnGrid}>
              <LoadingButton
                variant="contained"
                type="submit"
                color="primary"
                loading={loader}
                onClick={() => setModel(true)}
                sx={styles.bonusButton}
              >
                Add Bonus
              </LoadingButton>
            </Box>
          )}
          <Box sx={styles.btnPdf}>
            <LoadingButton
              variant="contained"
              type="submit"
              color="primary"
              loading={loading}
              disabled={isPayslipAccessDisabled || !data?.length || data?.some(item => !item?.isPaid)}
              onClick={() => handlePrintAll()}
              sx={styles.submitPdf}
            >
              Print All
            </LoadingButton>
          </Box>
          {!isEmployee && (
          <Box sx={styles.btnGrid}>
            <LoadingButton
              variant="contained"
              type="submit"
              color="primary"
              loading={loader}
              onClick={() => handleEmailAll()}
              disabled={!data?.length || data?.some(item => !item?.isPaid)}
              sx={styles.submitButton}
            >
              Email All
            </LoadingButton>
          </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.white.main,
          m: 2,
          borderRadius: "20px",
        }}
      >
        <SubHeader
          title="Payslips"
          search={true}
          sortBy={true}
          BtnText={dataAll?.allSalaries ? "All Processed" : "Process All"} // Conditional text
          // BtnText={"Process All"}

          onchange={handleText}
          onClick={handleNavigate}
          handleSort={handleSort}
          filter={handleFilter}
          isShowFilter={false}
          disabled={!data.length || dataAll?.allSalaries}
          btn={permission?.salary?.isEdit || role === "Admin" || role === "SuperAdmin"}
        />
        <SalaryManagementList
          data={data}
          count={count}
          page={page}
          setPage={setPage}
          handleUpdate={handleUpdate}
          payslipPDF={payslipPDF}
          handleShowPdf={handleShowPdf}
          isProcessed={isProcessed}
          isPayslipAccessDisabled={isPayslipAccessDisabled}
        />
      </Box>

      <Modal
        open={model}
        setOpen={setModel}
        btnText={"Add Bonus"}
        disabled={
          (!selectedOptions && !department) ||
          !bonus ||
          !date ||
          !to ||
          !fixed ||
          !bonusName ||
          (fixed === "Percentage" && !percentageType)
        }
        title={"Add Bonus"}
        handleCheck={() => {
          handleBonus(
            selectedOptions.map((option) => option._id),
            bonus,
            date,
            to,
            fixed,
            bonusName,
            percentageType
          );
          setModel(false);
        }}
        loading={loading}
      >
        <Box sx={styles.containerInput}>
          <Box sx={styles.dateGrid}>
            <Box sx={{ mr: 1, width: "50%" }}>
              <Autocomplete
                id="autocomplete"
                multiple
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    <ListItemText primary={getEmployeeEmail(option)} />
                  </MenuItem>
                )}
                options={empDataId || []}
                isOptionEqualToValue={(option, value) =>
                  getEmployeeId(option) === getEmployeeId(value)
                }
                renderTags={() =>
                  hasSelectedEmails ? (
                    <Typography
                      title={selectedEmailsText}
                      sx={{
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        px: 0.5,
                      }}
                    >
                      {selectedEmailsText}
                    </Typography>
                  ) : null
                }
                getOptionLabel={(option) => getEmployeeEmail(option)}
                onInputChange={(event, newInputValue) => {
                  SearchEmployee(newInputValue);
                }}
                onChange={(event, newValue) => {
                  handleChange(event, newValue);
                }}
                renderInput={(params) => (
                  <>
                    <InputLabel sx={styles.label}>Employee Email</InputLabel>
                    <TextField
                      {...params}
                      sx={{ ...styles.modalInput, ...styles.inputSpace }}
                      variant="outlined"
                      placeholder={
                        hasSelectedEmails ? "" : "Enter Employee Email"
                      }
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          ...(params.InputProps?.sx || {}),
                          height: "2.5em",
                          minHeight: "2.5em",
                          borderRadius: "8px",
                          alignItems: "center",
                          py: 0,
                        },
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  </>
                )}
              />
            </Box>
            <Box sx={{ width: "50%" }}>
              <InputLabel sx={styles.label}>Bonus Name</InputLabel>
              <TextField
                value={bonusName}
                name="bonusName"
                placeholder="Enter Bonus Name"
                onChange={(e) => {
                  setBonusName(e.target.value);
                }}
                sx={styles.modalInputs}
              />
            </Box>
          </Box>
          <Box sx={styles.dateGrid}>
            <Box sx={{ width: "100%", mt: 1, mr: 1 }}>
              <InputLabel sx={styles.label}>Department</InputLabel>
              <Select
                value={department}
                onChange={handleDepartmentChange}
                sx={{ ...styles.modalInput, borderRadius: "8px", my: "10px" }}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                <MenuItem value="All">All</MenuItem>
                {departmentData?.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item?.name}
                    onClick={() => DepartmentPayroll(item?.name)}
                  >
                    {item?.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ width: "100%", mt: 1 }}>
              <InputLabel sx={styles.label}>Bonus</InputLabel>
              <TextField
                value={bonus}
                name="bonus"
                placeholder="Enter Bonus"
                onChange={(e) => {
                  setBonus(e.target.value);
                }}
                sx={styles.modalInputs}
              />
            </Box>
          </Box>

          <Box sx={styles.dateGrid}>
            <Box sx={{ mr: 1, mt: 1, width: "50%" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={styles.label}>From</InputLabel>
                <DatePicker
                  value={date}
                  onChange={(value) => {
                    setDate(value);
                  }}
                  sx={styles.dateInput}
                  slotProps={{
                    field: {
                      readOnly: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ mt: 1, width: "50%" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={styles.label}>To</InputLabel>
                <DatePicker
                  value={to}
                  onChange={(value) => {
                    setTo(value);
                    handleDate(value, date, to);
                  }}
                  sx={styles.dateInput}
                  slotProps={{
                    field: {
                      readOnly: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>

            <CustomizedRadio
              handleChange={(val) => setFixed(val)}
              value={fixed}
              radioLabel={radioLabel}
            />
            {/* Show Percentage Type dropdown when Percentage is selected - Above radio buttons, 50% width */}
            {fixed === "Percentage" && (
              <Box sx={{ width: "50%" }}>
                <InputLabel sx={styles.label}>Select Type</InputLabel>
                <Select
                  value={percentageType}
                  onChange={(e) => setPercentageType(e.target.value)}
                  sx={{ ...styles.modalInput, borderRadius: "8px", width: "100%" }}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem disabled value="">
                    Select Type
                  </MenuItem>
                  <MenuItem value="Gross">Gross</MenuItem>
                  <MenuItem value="Salary">Salary</MenuItem>
                </Select>
              </Box>
            )}


          </Box>
        </Box>
      </Modal>
      <Modal
        open={holidayOpen}
        setOpen={setHolidayOpen}
        title={"Note"}
        loading={loading}
      >
        <Box>
          <Typography sx={styles.uploadText}> {popupMsg}</Typography>
          <Button variant="contained" color="primary" onClick={() => setHolidayOpen(false)}>
            Okay
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default SalaryManagementPage;
